import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { Building2 } from "lucide-react";
import type { CustomerClass } from "@hisense/shared";
import { useCanSwitchRegion } from "@/app/access";
import { setViewRegion, useViewRegion } from "@/app/viewRegion";

const OPTIONS: readonly (CustomerClass | null)[] = [null, "vietnam", "china"];

/**
 * Công tắc công ty ở thanh trên (yêu cầu v1.8 mục 1.1): tài khoản thấy cả hai công ty xem riêng Việt Nam hoặc Trung Quốc trên MỌI màn —
 * bảng điều khiển, phân tích, lợi nhuận, tình trạng PI, KPI, khách, nhà cung cấp, đơn mẫu, thẻ mẫu, bán hàng, mua hàng, sao kê.
 * Máy chủ thu hẹp phạm vi; đổi công ty là xoá dữ liệu đang đệm của công ty kia rồi tải lại, không bao giờ hiện lẫn.
 * Tài khoản một khu vực không thấy công tắc — họ vốn chỉ có một công ty.
 */
export function CompanySwitch() {
  const { t } = useTranslation();
  const canSwitch = useCanSwitchRegion();
  const view = useViewRegion();
  const cache = useQueryClient();
  // Đổi công ty là xoá dữ liệu đệm rồi tải lại — trang tạo / sửa đang mở sẽ dựng lại form và mất phần chưa lưu, không hỏi. Khoá công
  // tắc trong lúc tạo / sửa (mọi trang `/new`, `/:id/edit`): lưu hoặc huỷ trước rồi mới đổi (kiểm thử 11/10/2026).
  const { pathname } = useLocation();
  const editing = /\/(new|edit)\/?$/.test(pathname);
  if (!canSwitch) return null;
  const label = (x: CustomerClass | null) => (x === null ? t("companySwitch.all") : t(x === "china" ? "customers.china" : "customers.vietnam"));
  return (
    <div role="group" aria-label={t("companySwitch.label")} title={t(editing ? "companySwitch.lockedEditing" : "companySwitch.hint")} className="flex items-center gap-1.5">
      <Building2 className="hidden size-4 text-ink-faint sm:block" aria-hidden="true" />
      <div className="flex items-center rounded-sm border border-line bg-surface p-0.5">
        {OPTIONS.map((x) => (
          <button
            key={x ?? "all"}
            type="button"
            aria-pressed={view === x}
            disabled={editing && view !== x}
            onClick={() => {
              if (view === x || editing) return;
              setViewRegion(x);
              // Dữ liệu đệm là của công ty cũ: bỏ đi và tải lại các màn đang mở.
              void cache.resetQueries();
            }}
            className={
              "rounded-[3px] px-2 py-1 text-xs font-medium transition-colors " +
              (view === x ? "bg-brand text-brand-on" : "text-ink-faint hover:text-ink-soft disabled:cursor-not-allowed disabled:opacity-50")
            }
          >
            {label(x)}
          </button>
        ))}
      </div>
    </div>
  );
}
